import {
  type AnalyticsClient,
  type AnalyticsEventKey,
  type AnalyticsEventProperties,
  analyticsEventGoesTo,
  resolveAnalyticsEvent,
} from "@healthos/analytics";
import { readEnv } from "../env";
import { branchAnalytics } from "./analytics-branch";
import { datadogAnalytics } from "./analytics-datadog";
import { isInDesktopEmbed } from "./host-auth";

type SegmentBrowserClient = {
  identify(userId: string): unknown;
  reset(): unknown;
  page(name: string, properties: Record<string, unknown>): unknown;
  track(eventName: string, properties: Record<string, unknown>): unknown;
};

let clientPromise: Promise<SegmentBrowserClient | null> | null = null;

function getClient(): Promise<SegmentBrowserClient | null> {
  const writeKey = readEnv().segmentWriteKey;
  if (!writeKey) return Promise.resolve(null);

  const client =
    clientPromise ??
    import("@segment/analytics-next")
      .then(({ AnalyticsBrowser }) =>
        AnalyticsBrowser.load(
          { writeKey },
          {
            initialPageview: false,
            disableClientPersistence: true,
          },
        ),
      )
      .then(([analytics]) => analytics)
      .catch(() => null);
  clientPromise = client;
  return client;
}

// The same bundle serves the browser and the desktop sidecar's embedded
// webview, so the surface is resolved per call rather than at load: the embed
// check reads host state that is not guaranteed to be settled at import time.
function clientSurface(): "desktop" | "web" {
  return isInDesktopEmbed() ? "desktop" : "web";
}

function invoke(action: (client: SegmentBrowserClient) => unknown): void {
  void getClient()
    .then((client) => (client ? action(client) : undefined))
    .catch(() => undefined);
}

/**
 * Segment adapter for the shared event contract. The write key is public by
 * design. When it is absent, local development and tests remain no-ops.
 */
export const segmentAnalytics: AnalyticsClient = {
  identify(userId) {
    invoke((client) => client.identify(userId));
  },
  reset() {
    invoke((client) => client.reset());
  },
  page(screenName) {
    invoke((client) =>
      client.page(screenName, {
        screen_name: screenName,
        client_surface: clientSurface(),
      }),
    );
  },
  track<Key extends AnalyticsEventKey>(key: Key, properties: AnalyticsEventProperties[Key]) {
    if (!analyticsEventGoesTo(key, "segment")) return;
    const event = resolveAnalyticsEvent(key, properties);
    invoke((client) =>
      client.track(event.name, {
        ...event.properties,
        client_surface: clientSurface(),
      }),
    );
  },
};

/**
 * The one analytics entry point for the web client, fanning every call out to
 * both sinks. Call sites stay unaware of either: they name a semantic event
 * from the catalog and each sink decides whether it wants it (see
 * `analyticsEventGoesTo`) and how to shape it.
 *
 * Segment is the warehouse taxonomy the data team models on. Datadog RUM is
 * where the product funnels are built, because the events land in the same
 * session as the errors, the long tasks, and the Session Replay, so a drop-off
 * can be explained and not just counted. Branch is the third sink and by far
 * the narrowest: it answers only "did this download surface produce an
 * install", which neither of the other two can see past the App Store. It
 * takes a named allowlist rather than the whole catalog, and no member-derived
 * property at all (see `analytics-branch.ts`).
 */
export const analytics: AnalyticsClient = {
  identify(userId) {
    segmentAnalytics.identify(userId);
    datadogAnalytics.identify(userId);
    branchAnalytics.identify(userId);
  },
  reset() {
    segmentAnalytics.reset();
    datadogAnalytics.reset();
    branchAnalytics.reset();
  },
  page(screenName) {
    segmentAnalytics.page(screenName);
    datadogAnalytics.page(screenName);
    branchAnalytics.page(screenName);
  },
  track<Key extends AnalyticsEventKey>(key: Key, properties: AnalyticsEventProperties[Key]) {
    segmentAnalytics.track(key, properties);
    datadogAnalytics.track(key, properties);
    branchAnalytics.track(key, properties);
  },
};
